import React, { createContext, useContext, ReactNode } from 'react';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { SessionTimeoutWarning } from '../components/common/SessionTimeoutWarning';

interface SessionTimeoutContextType {
  setAudioPlaying: (playing: boolean) => void;
  updateActivity: () => void;
  extendSession: () => void;
  forceLogout: () => void;
  isExtended: boolean;
}

const SessionTimeoutContext = createContext<SessionTimeoutContextType | undefined>(undefined);

interface SessionTimeoutProviderProps {
  children: ReactNode;
  timeoutMinutes?: number;
  warningMinutes?: number;
}

export const SessionTimeoutProvider: React.FC<SessionTimeoutProviderProps> = ({
  children,
  timeoutMinutes = 30,
  warningMinutes = 5,
}) => {
  // CJIS 5.5.5: Fixed 30-minute inactivity timeout, no client-side overrides.
  // Only real user input (mouse, keyboard, touch) resets the timer.
  // API calls from background polling do NOT reset it.
  const {
    isWarningVisible,
    remainingSeconds,
    isExtended,
    extendSession,
    setAudioPlaying,
    updateActivity,
    forceLogout,
  } = useSessionTimeout({
    timeoutMinutes,
    warningMinutes,
  });

  const contextValue: SessionTimeoutContextType = {
    setAudioPlaying,
    updateActivity,
    extendSession,
    forceLogout,
    isExtended,
  };

  return (
    <SessionTimeoutContext.Provider value={contextValue}>
      {children}
      <SessionTimeoutWarning
        open={isWarningVisible}
        remainingSeconds={remainingSeconds}
        onExtend={extendSession}
        onLogout={forceLogout}
      />
    </SessionTimeoutContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSessionTimeoutContext = (): SessionTimeoutContextType => {
  const context = useContext(SessionTimeoutContext);
  if (!context) {
    throw new Error('useSessionTimeoutContext must be used within a SessionTimeoutProvider');
  }
  return context;
};
